import React, { FC, useCallback, useEffect } from "react";
import { CommandStatus, QueryStatus } from "@lookiero/messaging-react";
import { useLogger } from "@lookiero/sty-psp-logging";
import { GameProductVariantProjection } from "../../../projection/game/game";
import { usePlayGame } from "../../domain/game/react/usePlayGame";
import { useViewGameByCustomerId } from "../../projection/game/react/useViewGameByCustomerId";
import {
  BaseLookAndLikeProps,
  LookAndLike as LookAndLikeComponent,
} from "../components/templates/lookAndLike/LookAndLike";

interface HandleOnChangeFunctionArgs {
  readonly id: string;
  readonly like: boolean;
}

interface HandleOnChangeFunction {
  (args: HandleOnChangeFunctionArgs): void;
}

interface OnFetchedFunctionArgs {
  readonly gameProductVariants: GameProductVariantProjection[];
  readonly lastGame: boolean;
}

interface OnFetchedFunction {
  (args: OnFetchedFunctionArgs): void;
}

interface LookAndLikeProps extends BaseLookAndLikeProps {
  readonly customerId: string;
  readonly view: string;
  readonly standalone?: boolean;
  readonly onError?: () => void;
  readonly onFetched?: OnFetchedFunction;
  readonly onReady?: () => void;
}

const LookAndLike: FC<LookAndLikeProps> = ({
  customerId,
  view,
  standalone = false,
  onError,
  onFetched,
  onReady,
  ...props
}) => {
  const logger = useLogger();
  const [game, gameStatus] = useViewGameByCustomerId({ customerId });
  const [playGame, playGameStatus] = usePlayGame({ view, logger });

  const handleOnChange: HandleOnChangeFunction = useCallback(playGame, [playGame]);

  useEffect(() => {
    if (gameStatus === QueryStatus.ERROR || playGameStatus === CommandStatus.ERROR) {
      onError?.();
    }
  }, [gameStatus, onError, playGameStatus]);

  useEffect(() => {
    if (game) {
      onFetched?.({ gameProductVariants: game?.gameProductVariants || [], lastGame: game?.lastGame || false });
    }
  }, [game, onFetched]);

  const dependenciesLoadedStatuses = [QueryStatus.ERROR, QueryStatus.SUCCESS];
  const dependenciesLoaded = dependenciesLoadedStatuses.includes(gameStatus);

  useEffect(() => {
    if (!dependenciesLoaded || game?.gameProductVariants.length === 0) {
      return;
    }

    onReady?.();
  }, [dependenciesLoaded, game?.gameProductVariants.length, onReady]);

  if (!dependenciesLoaded || ((game === null || game?.gameProductVariants.length === 0) && !standalone)) {
    return null;
  }

  return (
    <LookAndLikeComponent
      {...props}
      gameProductVariants={game?.gameProductVariants || []}
      lastGame={game?.lastGame || false}
      onChange={handleOnChange}
    />
  );
};

export type { LookAndLikeProps };
export { LookAndLike };
